import { useMemo } from 'react';

import { AuthCancelMessages } from 'features/analytics/constants';
import { DiagnosticContextSurface } from 'features/diagnostics/components/diagnostic-context-surface';
import { StyleSheet } from 'react-native';
import { Divider, Icon, Pressable } from 'ucl/components';

import { BottomSheet } from 'components/bottom-sheet';
import { Box, Typography } from 'components/ui';
import { KeyboardAwareBottomSheetContent } from 'components/ui/keyboard-aware-bottom-sheet-content';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import { useIsDesktopWebBreakpoint } from 'hooks/use-media-query';
import { usePrevious } from 'hooks/use-previous';
import SignUpHeadline from 'pages/authentication/components/sign-up-headline/sign-up-headline';
import ConfirmOtp from 'pages/authentication/confirm-otp';
import { SignInForm } from 'pages/authentication/sign-in/sign-in-form';
import SignUp from 'pages/authentication/sign-up';
import { useAuthContext } from 'state/auth';
import { ModalAuthScreen } from 'state/auth/types';
import { t } from 'state/intl/translations';
import { getDesignToken, spacing } from 'styles/constants';
import { WEB_APP_MODAL_CONTENT_MAX_WIDTH } from 'utils/constants';

import { authLogCancel } from './utils';

export const ModalAuth = () => {
  const { modalAuthState, modalAuthIsOpen, closeModalAuth, openSignInModal } = useAuthContext();

  const getModalAuthComponent = () =>
    ModalAuthComponents[modalAuthState.screen || ModalAuthScreen.CLOSED];

  const isSignInPage = modalAuthState.screen === ModalAuthScreen.SIGN_IN;
  const isSignUpPage = modalAuthState.screen === ModalAuthScreen.SIGN_UP;
  const isDesktopWeb = useIsDesktopWebBreakpoint();
  const shouldShowModalAuthHeadline = !isSignUpPage;

  const previousScreen = usePrevious<ModalAuthScreen>(
    modalAuthState.screen ?? ModalAuthScreen.CLOSED
  );

  const handleBackNavigation = () => {
    modalAuthState.screen &&
      authLogCancel(
        AuthCancelMessages.USER_NAVIGATED_BACK_USING_BACK_BUTTON,
        modalAuthState.screen
      );

    isSignInPage ? closeModalAuth() : openSignInModal();
  };

  const ModalAuthComponents = useMemo(
    () => ({
      [ModalAuthScreen.SIGN_IN]: <SignInForm />,
      [ModalAuthScreen.SIGN_UP]: (
        <SignUp
          skipRedirect
          shouldDisableHeadlineLinks={modalAuthState.shouldDisableHeadlineLinks}
        />
      ),
      [ModalAuthScreen.OTP]: <ConfirmOtp />,
      [ModalAuthScreen.CLOSED]: null,
    }),
    [modalAuthState.shouldDisableHeadlineLinks]
  );

  useEffectOnUpdates(() => {
    if (modalAuthState.screen !== ModalAuthScreen.CLOSED) {
      return;
    }
    previousScreen && authLogCancel(AuthCancelMessages.USER_CLOSED_AUTH_MODAL, previousScreen);
  }, [modalAuthState.screen]);

  if (!modalAuthIsOpen) {
    return null;
  }

  return (
    <BottomSheet
      isOpen
      contentStyle={styles.sheet}
      onOpenEventMessage="Open modal authentication"
      onClose={closeModalAuth}
      presentationOverlay={<DiagnosticContextSurface isModal />}
    >
      <KeyboardAwareBottomSheetContent>
        <Box style={styles.header}>
          {!isSignInPage && (
            <Pressable
              accessibilityLabel={t('back')}
              accessibilityRole="button"
              onPress={handleBackNavigation}
              style={styles.headerButton}
            >
              <Icon
                name="custom:Back"
                color={getDesignToken('icon-default')}
                title={t('back')}
                size={5}
              />
            </Pressable>
          )}
          <Box style={styles.headerSpacer} />
          <Pressable
            onPress={closeModalAuth}
            accessible
            accessibilityRole="button"
            accessibilityLabel={t('close')}
            style={[styles.headerButton, styles.closeButton]}
          >
            <Icon variant="cancel" color={getDesignToken('icon-default')} title={t('cancel')} />
          </Pressable>
        </Box>
        <Box style={styles.content}>
          <Box style={[styles.innerContent, isDesktopWeb && styles.innerContentDesktopWeb]}>
            {shouldShowModalAuthHeadline && (
              <>
                <SignUpHeadline disableLink={modalAuthState.shouldDisableHeadlineLinks} />
                <Divider background="blackOpacity.30" marginTop="$6" marginBottom="$6" />
              </>
            )}
            {modalAuthState.heading ? (
              <Typography style={styles.heading}>{modalAuthState.heading}</Typography>
            ) : null}
            {getModalAuthComponent()}
          </Box>
        </Box>
      </KeyboardAwareBottomSheetContent>
    </BottomSheet>
  );
};

const styles = StyleSheet.create({
  closeButton: {
    marginLeft: 'auto',
  },
  content: {
    alignSelf: 'center',
    flexGrow: 1,
    maxWidth: WEB_APP_MODAL_CONTENT_MAX_WIDTH,
    width: '100%',
  },
  header: {
    flexDirection: 'row',
    height: spacing.$11,
    padding: spacing.$2,
  },
  headerButton: {
    justifyContent: 'center',
  },
  headerSpacer: {
    flex: 1,
  },
  heading: {
    marginBottom: spacing.$6,
  },
  innerContent: {
    flexGrow: 1,
    paddingVertical: spacing.$4,
  },
  innerContentDesktopWeb: {
    paddingHorizontal: spacing.$32,
  },
  sheet: {
    overflow: 'hidden',
  },
});
